import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { conversationService } from "@/services/conversationService";
import { toast } from "sonner";
import { useCallback } from "react";

export const useConversations = () => {
  const queryClient = useQueryClient();

  const conversationsQuery = useQuery({
    queryKey: ["conversations"],
    queryFn: () => conversationService.getConversations(),
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos no cache
    refetchOnWindowFocus: true,
    // refetchInterval: 30 * 1000, // Polling desativado para teste WebSocket
  });

  return {
    conversations: conversationsQuery.data || [],
    isLoading: conversationsQuery.isLoading,
    error: conversationsQuery.error,
    refetch: conversationsQuery.refetch,
  };
};

// Hook para buscar uma conversa específica com mensagens
export const useConversation = (id: string) => {
  const conversationQuery = useQuery({
    queryKey: ["conversations", id],
    queryFn: () => conversationService.getConversationById(id),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos no cache
    refetchOnWindowFocus: true,
    // refetchInterval: 10 * 1000, // Polling desativado para teste WebSocket
    refetchIntervalInBackground: false,
  });

  return {
    data: conversationQuery.data,
    isLoading: conversationQuery.isLoading,
    error: conversationQuery.error,
    refetch: conversationQuery.refetch,
  };
};

// Hook para atualizar useAi de uma conversa
export const useUpdateConversationAi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, useai }: { id: string; useai: boolean }) =>
      conversationService.updateUseAi(id, useai),
    onSuccess: (updatedConversation) => {
      // Atualizar a lista de conversas
      queryClient.setQueryData(["conversations"], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.map((conv: any) =>
          conv.id === updatedConversation.id ? updatedConversation : conv
        );
      });

      // Atualizar a conversa específica se estiver em cache
      queryClient.setQueryData(
        ["conversations", updatedConversation.id],
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            useai: updatedConversation.useai,
          };
        }
      );

      toast.success("Configuração de IA atualizada com sucesso!");
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || "Erro ao atualizar configuração de IA";
      toast.error(message);
    },
  });
};

// Hook para enviar mensagem
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      message,
    }: {
      conversationId: string;
      message: string;
    }) => conversationService.sendMessage(conversationId, message),
    onSuccess: (_, { conversationId }) => {
      // Invalidar queries para atualizar as mensagens
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({
        queryKey: ["conversations", conversationId],
      });

      toast.success("Mensagem enviada com sucesso!");
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || "Erro ao enviar mensagem";
      toast.error(message);
    },
  });
};
